import React, { useCallback, useContext, useEffect, useState } from "react";

import { ZeosApiContext } from "../../context/ZeosApiContext";
import {
  IPropertyMeterContract,
  IPropertyMetersContract,
  METER_CLASS_ID,
  METER_GROUPING_THRESHOLD,
  METER_OCCUPANCY_OPTIONS,
  METER_ROLE,
  METER_ROLE_OPTIONS,
} from "../../services/zeos-api/contracts/Meter";
import { invalidateDashboardOverviewCache } from "../Dashboard/MainDashboard";
import { invalidateNextGenSeriesCache } from "../NextGenDashboard/NextGenDashboard";
import { CoverageLedger } from "./CoverageLedger";
import { LinkAnotherMeter } from "./LinkAnotherMeter";

// MTR-P1-09 meters list + classification (task-4062, spec v0.3 R5 / R13). For company Admins and super
// admins; the API enforces it. A flat list below METER_GROUPING_THRESHOLD live meters, grouped by class at or
// above it, with UNASSIGNED always last so untriaged meters stay visible. Retired meters are collapsed at the
// bottom. Totals use the new classification on the next dashboard load (the rollup is computed live), so a
// save drops this property's cached dashboard data.

const isSuccess = (status?: string) => status?.toLowerCase() === "success";

const titleCase = (value?: string | null) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "";

const formatDate = (value?: string | null) => (value ? value.slice(0, 10) : "—");

const meterName = (meter: IPropertyMeterContract) =>
  meter.meterUid || meter.meterNumber || `Meter ${meter.meterId}`;

interface IDraft {
  meterClassId: string;
  displayLabel: string;
  rollupRole: string;
  parentMeterId: string;
  occupancyState: string;
  statusNote: string;
}

const toDraft = (meter: IPropertyMeterContract): IDraft => ({
  meterClassId: meter.meterClassId === METER_CLASS_ID.Unassigned ? "" : String(meter.meterClassId),
  displayLabel: meter.displayLabel ?? "",
  rollupRole: meter.rollupRole || METER_ROLE.Primary,
  parentMeterId: meter.parentMeterId ? String(meter.parentMeterId) : "",
  occupancyState: meter.occupancyState || "UNKNOWN",
  statusNote: meter.rollupRole === METER_ROLE.Excluded ? meter.statusNote ?? "" : "",
});

// Same rules as MeterClassificationRules.cs, so the Save button only enables for an edit the API accepts.
export const draftError = (draft: IDraft): string | null => {
  if (draft.meterClassId === "") return "Choose a meter class.";
  const classId = Number(draft.meterClassId);
  if (classId === METER_CLASS_ID.Other && draft.displayLabel.trim() === "") {
    return "Other needs a label that says what the meter is.";
  }
  const isGeneration = classId === METER_CLASS_ID.Generation;
  const role = isGeneration ? METER_ROLE.Excluded : draft.rollupRole;
  if (role === METER_ROLE.Sub && draft.parentMeterId === "") return "A sub-meter needs a parent meter.";
  if (role === METER_ROLE.Excluded && !isGeneration && draft.statusNote.trim() === "") {
    return "Say why this meter is excluded.";
  }
  return null;
};

interface IProps {
  propertyId: number;
  /** Called after a meter is saved, e.g. to refresh a steward queue. */
  onChanged?: () => void;
}

export const PropertyMetersPanel = ({ propertyId, onChanged }: IProps) => {
  const api = useContext(ZeosApiContext);
  const [data, setData] = useState<IPropertyMetersContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<IDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showRetired, setShowRetired] = useState(false);
  const [linking, setLinking] = useState(false);
  // Bumped after a save or a link so the coverage ledger reloads (task-4051).
  const [ledgerKey, setLedgerKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.Meter.getPropertyMetersAsync(propertyId);
      if (isSuccess(response.zeosStatus) && response.data) {
        setData(response.data);
      } else {
        setError(response.message || "Could not load meters.");
      }
    } catch {
      setError("Could not load meters.");
    } finally {
      setLoading(false);
    }
  }, [api, propertyId]);

  useEffect(() => {
    load();
  }, [load]);

  const meters = data?.meters ?? [];
  // Setup records name the utility but aren't meters (OI-05): listed apart, not counted, never edited.
  const live = meters.filter((m) => !m.isRetired && !m.isSetupRecord);
  const setupRecords = meters.filter((m) => !m.isRetired && m.isSetupRecord);
  const retired = meters.filter((m) => m.isRetired);
  const unassignedCount = live.filter((m) => m.meterClassId === METER_CLASS_ID.Unassigned).length;
  const byId = new Map(meters.map((m) => [m.meterId, m]));

  // Class groups in reference order, UNASSIGNED last.
  const classOrder = new Map((data?.meterClasses ?? []).map((c) => [c.meterClassId, c.sortOrder]));
  const groupMap = new Map<number, IPropertyMeterContract[]>();
  for (const meter of live) {
    groupMap.set(meter.meterClassId, [...(groupMap.get(meter.meterClassId) ?? []), meter]);
  }
  const groups = [...groupMap.entries()].sort(([a], [b]) => {
    if (a === METER_CLASS_ID.Unassigned) return 1;
    if (b === METER_CLASS_ID.Unassigned) return -1;
    return (classOrder.get(a) ?? 999) - (classOrder.get(b) ?? 999);
  });

  const startEdit = (meter: IPropertyMeterContract) => {
    setEditingId(meter.meterId);
    setDraft(toDraft(meter));
    setSaveError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
    setSaveError("");
  };

  const updateDraft = (change: Partial<IDraft>) => setDraft((d) => (d ? { ...d, ...change } : d));

  const save = async (meter: IPropertyMeterContract) => {
    if (!draft || draftError(draft)) return;
    const classId = Number(draft.meterClassId);
    const role = classId === METER_CLASS_ID.Generation ? METER_ROLE.Excluded : draft.rollupRole;
    setSaving(true);
    setSaveError("");
    try {
      const response = await api.Meter.classifyMeterAsync(propertyId, meter.meterId, {
        meterClassId: classId,
        displayLabel: draft.displayLabel.trim(),
        rollupRole: role,
        parentMeterId: role === METER_ROLE.Sub ? Number(draft.parentMeterId) : null,
        occupancyState: draft.occupancyState,
        // Null keeps the note; the API clears an exclusion reason when a meter stops being excluded.
        statusNote: role === METER_ROLE.Excluded ? draft.statusNote.trim() : null,
      });
      if (isSuccess(response.zeosStatus) && response.data) {
        const saved = response.data;
        setData((d) => (d ? { ...d, meters: d.meters.map((m) => (m.meterId === saved.meterId ? saved : m)) } : d));
        cancelEdit();
        invalidateDashboardOverviewCache(propertyId);
        invalidateNextGenSeriesCache(propertyId);
        setLedgerKey((k) => k + 1);
        onChanged?.();
      } else {
        setSaveError(response.message || "Could not save the meter.");
      }
    } catch {
      setSaveError("Could not save the meter.");
    } finally {
      setSaving(false);
    }
  };

  const classText = (meter: IPropertyMeterContract) => {
    if (meter.meterClassId === METER_CLASS_ID.Unassigned) return "Unassigned";
    const name = meter.meterClassName ?? meter.meterClassCode ?? "";
    return meter.displayLabel ? `${name}: ${meter.displayLabel}` : name;
  };

  const roleText = (meter: IPropertyMeterContract) => {
    if (meter.rollupRole === METER_ROLE.Sub && meter.parentMeterId) {
      const parent = byId.get(meter.parentMeterId);
      return `Sub of ${parent ? meterName(parent) : `meter ${meter.parentMeterId}`}`;
    }
    return titleCase(meter.rollupRole);
  };

  const renderEditor = (meter: IPropertyMeterContract) => {
    if (!draft) return null;
    const classId = draft.meterClassId === "" ? null : Number(draft.meterClassId);
    const isGeneration = classId === METER_CLASS_ID.Generation;
    const role = isGeneration ? METER_ROLE.Excluded : draft.rollupRole;
    const parentOptions = live.filter(
      (m) => m.meterId !== meter.meterId && m.utilityTypeId === meter.utilityTypeId && m.rollupRole !== METER_ROLE.Sub
    );
    const problem = draftError(draft);
    const name = meterName(meter);
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-gray-50 border border-gray-200 rounded">
        <label className="text-xs text-gray-700">
          Class
          <select
            aria-label={`Class for ${name}`}
            className="block w-full mt-1 text-sm border border-gray-300 rounded px-2 py-1"
            value={draft.meterClassId}
            onChange={(e) => updateDraft({ meterClassId: e.target.value })}
            disabled={saving}
          >
            <option value="">Choose…</option>
            {(data?.meterClasses ?? []).map((c) => (
              <option key={c.meterClassId} value={c.meterClassId}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-gray-700">
          Label{classId === METER_CLASS_ID.Other ? " (required)" : ""}
          <input
            type="text"
            maxLength={100}
            aria-label={`Label for ${name}`}
            className="block w-full mt-1 text-sm border border-gray-300 rounded px-2 py-1"
            value={draft.displayLabel}
            onChange={(e) => updateDraft({ displayLabel: e.target.value })}
            disabled={saving}
          />
        </label>
        <label className="text-xs text-gray-700">
          Role
          <select
            aria-label={`Role for ${name}`}
            className="block w-full mt-1 text-sm border border-gray-300 rounded px-2 py-1"
            value={role}
            onChange={(e) => updateDraft({ rollupRole: e.target.value })}
            disabled={saving || isGeneration}
          >
            {METER_ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {isGeneration && <span className="block mt-1 text-gray-500">Generation is never counted as consumption.</span>}
        </label>
        {role === METER_ROLE.Sub && (
          <label className="text-xs text-gray-700">
            Parent meter
            <select
              aria-label={`Parent for ${name}`}
              className="block w-full mt-1 text-sm border border-gray-300 rounded px-2 py-1"
              value={draft.parentMeterId}
              onChange={(e) => updateDraft({ parentMeterId: e.target.value })}
              disabled={saving}
            >
              <option value="">Choose…</option>
              {parentOptions.map((m) => (
                <option key={m.meterId} value={m.meterId}>
                  {meterName(m)}
                  {m.serviceAddress ? ` · ${m.serviceAddress}` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="text-xs text-gray-700">
          Occupancy
          <select
            aria-label={`Occupancy for ${name}`}
            className="block w-full mt-1 text-sm border border-gray-300 rounded px-2 py-1"
            value={draft.occupancyState}
            onChange={(e) => updateDraft({ occupancyState: e.target.value })}
            disabled={saving}
          >
            {METER_OCCUPANCY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {role === METER_ROLE.Excluded && !isGeneration && (
          <label className="text-xs text-gray-700 md:col-span-3">
            Why is it excluded?
            <textarea
              maxLength={500}
              aria-label={`Exclusion reason for ${name}`}
              className="block w-full mt-1 text-sm border border-gray-300 rounded px-2 py-1"
              rows={2}
              value={draft.statusNote}
              onChange={(e) => updateDraft({ statusNote: e.target.value })}
              disabled={saving}
            />
          </label>
        )}
        <div className="md:col-span-3 flex items-center gap-3">
          <button
            type="button"
            aria-label={`Save ${name}`}
            className="text-sm text-zeos-orange-dk hover:underline disabled:opacity-50"
            onClick={() => save(meter)}
            disabled={saving || problem !== null}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" className="text-sm text-gray-600 hover:underline" onClick={cancelEdit} disabled={saving}>
            Cancel
          </button>
          {problem && <span className="text-xs text-gray-500">{problem}</span>}
          {saveError && (
            <span role="alert" className="text-xs text-red-600">
              {saveError}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderRow = (meter: IPropertyMeterContract) => (
    <li key={meter.meterId} className="py-2 border-t border-gray-100" data-testid={`meter-${meter.meterId}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-[12rem]">
          <div className="text-sm font-medium">{meterName(meter)}</div>
          <div className="text-xs text-gray-500">
            {[meter.serviceName, meter.source].filter(Boolean).join(" · ")}
            {meter.serviceAccountNumber && (
              <>
                {(meter.serviceName || meter.source) && " · "}
                <span className="ph-mask">SAID {meter.serviceAccountNumber}</span>
              </>
            )}
          </div>
          {meter.serviceAddress && <div className="text-xs text-gray-500">{meter.serviceAddress}</div>}
        </div>
        <div className="text-sm min-w-[10rem]">
          <div className={meter.meterClassId === METER_CLASS_ID.Unassigned ? "text-orange-600 font-semibold" : ""}>
            {classText(meter)}
          </div>
          <div className="text-xs text-gray-500">{roleText(meter)}</div>
        </div>
        <div className="text-xs text-gray-600 min-w-[8rem]">
          <div>{titleCase(meter.connectionStatus)}</div>
          <div>Last reported {formatDate(meter.lastReportedDate)}</div>
          {meter.isRetired && <div>Retired {formatDate(meter.activeTo)}</div>}
        </div>
        {editingId !== meter.meterId && (
          <button
            type="button"
            aria-label={`Edit ${meterName(meter)}`}
            className="text-sm text-zeos-orange-dk hover:underline disabled:opacity-50"
            onClick={() => startEdit(meter)}
            disabled={editingId !== null}
          >
            Edit
          </button>
        )}
      </div>
      {meter.statusNote && <p className="text-xs text-gray-500 italic mt-1">{meter.statusNote}</p>}
      {editingId === meter.meterId && <div className="mt-2">{renderEditor(meter)}</div>}
    </li>
  );

  const renderSetupRecord = (record: IPropertyMeterContract) => (
    <li key={record.meterId} className="py-2 border-t border-gray-100" data-testid={`setup-${record.meterId}`}>
      <div className="text-sm font-medium">{record.utilityName || "Utility"} selected</div>
      <div className="text-xs text-gray-500">
        {[record.serviceName, "no meter connected yet"].filter(Boolean).join(" · ")}
      </div>
    </li>
  );

  if (loading) return <p className="text-sm text-gray-600">Loading meters…</p>;
  if (error)
    return (
      <p role="alert" className="text-sm text-red-600">
        {error}
      </p>
    );
  if (!data) return null;

  if (linking) {
    return (
      <LinkAnotherMeter
        propertyId={propertyId}
        propertyName={data.propertyName}
        onFinished={(linked) => {
          setLinking(false);
          load();
          setLedgerKey((k) => k + 1);
          if (linked) onChanged?.();
        }}
      />
    );
  }

  const grouped = live.length >= METER_GROUPING_THRESHOLD;
  const className = (classId: number) =>
    classId === METER_CLASS_ID.Unassigned
      ? "Unassigned"
      : data.meterClasses.find((c) => c.meterClassId === classId)?.name ?? `Class ${classId}`;

  return (
    <div className="max-h-[75vh] overflow-y-auto pr-2">
      <div className="flex items-center justify-between gap-3 pr-6">
        <h3 className="text-lg font-semibold text-black">Meters · {data.propertyName}</h3>
        {/* R1: no add-a-meter on a confirmed single-meter property (task-4061). */}
        {!data.isSingleMeterInvisible && (
          <button
            type="button"
            className="text-sm text-zeos-orange-dk hover:underline disabled:opacity-50"
            onClick={() => setLinking(true)}
            disabled={editingId !== null}
          >
            Link another meter
          </button>
        )}
      </div>
      <p className="text-xs text-gray-600 mb-2">
        {live.length} meter{live.length === 1 ? "" : "s"}
        {unassignedCount > 0 ? ` · ${unassignedCount} unassigned` : ""}. Only primary meters count toward building
        totals; sub-meters and excluded meters stay listed but are not added.
      </p>
      {data.isSingleMeterInvisible && (
        <p className="text-xs text-gray-500 mb-2">
          Single-metered property: customers don't see meter tools here.
        </p>
      )}
      <CoverageLedger propertyId={propertyId} refreshKey={ledgerKey} />
      {live.length === 0 &&<p className="text-sm text-gray-600">No utility meters linked yet.</p>}
      {!grouped && live.length > 0 && <ul>{live.map(renderRow)}</ul>}
      {grouped &&
        groups.map(([classId, list]) => (
          <section key={classId} className="mt-3" aria-label={`${className(classId)} meters`}>
            <h4 className="text-sm font-semibold text-gray-800">
              {className(classId)} ({list.length})
            </h4>
            <ul>{list.map(renderRow)}</ul>
          </section>
        ))}
      {setupRecords.length > 0 && <ul>{setupRecords.map(renderSetupRecord)}</ul>}
      {retired.length > 0 && (
        <div className="mt-4">
          <button type="button" className="text-xs text-gray-600 hover:underline" onClick={() => setShowRetired((s) => !s)}>
            {showRetired ? "Hide" : "Show"} retired meters ({retired.length})
          </button>
          {showRetired && <ul>{retired.map(renderRow)}</ul>}
        </div>
      )}
    </div>
  );
};
